import React, { useState } from 'react';
// import { useNavigate } from 'react-router-dom'; // Keep if you use it elsewhere
import { motion, AnimatePresence } from 'framer-motion';

import { walkthroughData, getStepBackground } from '../data/walkthroughData'; // Assuming these paths are correct
import IPhoneMockup from '../components/IPhoneMockup'; // Assuming this path is correct
import AnimatedChatUI from '../components/AnimatedChatUI'; // Assuming this path is correct
import './InteractiveWalkthroughPage.css'; // Styling for this page

// Helper function to determine OS and return appropriate download link/text
const getOSSpecificLink = () => {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    const iOSLink = "https://apps.apple.com/in/app/roomease-roommate-finder/id6743614619";
    const androidLink = "https://play.google.com/store/apps/details?id=com.roomease.roomate&pli=1";
    const genericLink = "https://linktr.ee/roomeaseindia";

    if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
        return { text: "Download on the App Store", link: iOSLink, icon: '🍎', os: 'iOS' }; // Added os
    }
    if (/Android/.test(userAgent)) {
        return { text: "Get it on Google Play", link: androidLink, icon: '🤖', os: 'Android' }; // Added os
    }
    return { text: "Download now!", link: genericLink, icon: '', os: 'Unknown' }; // Added os
};

const InteractiveWalkthroughPage = () => {
    const [currentStepIndex, setCurrentStepIndex] = useState(0);
    // const navigate = useNavigate(); // Uncomment if you need routing

    const textColumnVariants = {
        initial: { opacity: 0, y: 30 },
        animate: {
            opacity: 1,
            y: 0,
            transition: {
                duration: 0.6,
                ease: "easeOut",
                when: "beforeChildren",
                staggerChildren: 0.05
            }
        },
        exit: {
            opacity: 0,
            y: -30,
            transition: { duration: 0.4, ease: "easeIn" }
        },
    };

    const textVariants = {
        initial: { opacity: 0, y: 15 },
        animate: { opacity: 1, y: 0 },
        exit: { opacity: 0, y: -10 }
    };

    const iPhoneContentVariants = {
        initial: { opacity: 0, scale: 0.98 },
        animate: { opacity: 1, scale: 1, transition: { duration: 0.4, ease: "easeOut" } },
        exit: { opacity: 0, scale: 0.98, transition: { duration: 0.2, ease: "easeIn" } },
    };

    const handleNextStep = () => {
        if (currentStepIndex < walkthroughData.length - 1) {
            setCurrentStepIndex(currentStepIndex + 1);
        }
    };

    const handlePreviousStep = () => {
        if (currentStepIndex > 0) {
            setCurrentStepIndex(currentStepIndex - 1);
        }
    };

    if (!walkthroughData || walkthroughData.length === 0) {
        return <div className="error-message">Error: Walkthrough data not loaded or is empty.</div>;
    }

    const currentStep = walkthroughData[currentStepIndex];

    if (!currentStep) {
        return <div className="error-message">Error: Current walkthrough step not found.</div>;
    }

    const osSpecificDownload = getOSSpecificLink();
    const isChatAnimationStep = currentStep.id === 'step-06';

    const handleWalkthroughDownloadClick = () => {
        // --- Facebook Pixel Tracking for Walkthrough Download App Click ---
        if (window.fbq) {
          window.fbq('track', 'Lead', {
            content_name: 'Walkthrough Page Download App Button Click',
            source_os: osSpecificDownload.os,
          });
          console.log(`Facebook Pixel: Lead event fired for Walkthrough Download App Button (${osSpecificDownload.os}).`);
        } else {
          console.warn('Facebook Pixel: fbq not found, Walkthrough Download App event not tracked.');
        }
        // --- End Facebook Pixel Tracking ---
        // The href on the <a> tag will handle the actual navigation
    };


    return (
        <motion.div
            className="interactive-walkthrough-page-wrapper"
            style={{ background: getStepBackground(currentStepIndex) }}
            transition={{ duration: 1.0, ease: "easeInOut" }}
        >
            <div className="walkthrough-container">
                <motion.h1
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, delay: 0.2, ease: "easeOut" }}
                >
                    Discover RoomEase: It's Magical!
                </motion.h1>

                <div className="walkthrough-content-area">

                    {/* Left Navigation Button (Desktop View Only) */}
                    <motion.div
                        className="walkthrough-navigation-side prev"
                        variants={textColumnVariants}
                        initial="initial"
                        animate="animate"
                        exit="exit"
                    >
                        <motion.button
                            onClick={handlePreviousStep}
                            disabled={currentStepIndex === 0}
                            className="walkthrough-button side-button"
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            transition={{ type: "spring", stiffness: 400, damping: 10 }}
                            aria-label="Previous Step"
                        >
                            <i className="fas fa-chevron-left"></i>
                        </motion.button>
                    </motion.div>

                    {/* This is the iPhone & Progress Dots Column */}
                    <div className="walkthrough-iphone-column">
                        <IPhoneMockup>
                            {isChatAnimationStep ? (
                                // Render chat animation for step-06
                                <AnimatedChatUI />
                            ) : (
                                // Otherwise, render the GIF for other steps (1-5)
                                currentStep.image && (
                                    <AnimatePresence mode="wait">
                                        <motion.img
                                            key={currentStep.image} // Key helps Framer Motion animate changes
                                            src={currentStep.image}
                                            alt={currentStep.alt || currentStep.title}
                                            className="walkthrough-image" // Ensure your CSS styles this
                                            variants={iPhoneContentVariants} // Apply animation variants
                                            initial="initial"
                                            animate="animate"
                                            exit="exit"
                                        />
                                    </AnimatePresence>
                                )
                            )}
                        </IPhoneMockup>

                        {/* Progress Dots (Visible on both desktop and mobile) */}
                        <div className="walkthrough-progress-dots">
                            {walkthroughData.map((_, index) => (
                                <motion.div
                                    key={index}
                                    className={`dot ${index === currentStepIndex ? 'active' : ''}`}
                                    onClick={() => setCurrentStepIndex(index)}
                                    whileHover={{ scale: 1.2 }}
                                    whileTap={{ scale: 0.9 }}
                                    transition={{ type: "spring", stiffness: 400, damping: 17 }}
                                    aria-label={`Go to step ${index + 1}`}
                                />
                            ))}
                        </div>
                    </div>

                    {/* This is the Text Column */}
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={currentStep.id + "-text"}
                            className="walkthrough-text-column"
                            variants={textColumnVariants}
                            initial="initial"
                            animate="animate"
                            exit="exit"
                        >
                            <motion.h2 variants={textVariants}>{currentStep.title}</motion.h2>
                            <motion.p variants={textVariants}>{currentStep.description}</motion.p>

                            {currentStep.additionalText && (
                                <motion.p variants={textVariants} className="additional-info">
                                    {currentStep.additionalText}
                                </motion.p>
                            )}

                            {currentStepIndex === walkthroughData.length - 1 && (
                                <motion.div
                                    variants={textVariants}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0, transition: { delay: 0.2, duration: 0.6, ease: "easeOut" } }}
                                    className="walkthrough-cta"
                                >
                                    <motion.h3 variants={textVariants}>Ready to Explore RoomEase?</motion.h3>
                                    <motion.a
                                        href={osSpecificDownload.link}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="download-button single-download-button"
                                        whileHover={{ scale: 1.05 }}
                                        whileTap={{ scale: 0.95 }}
                                        transition={{ type: "spring", stiffness: 400, damping: 10 }}
                                        variants={textVariants}
                                        onClick={handleWalkthroughDownloadClick}
                                    >
                                        {osSpecificDownload.icon && <span className="button-icon">{osSpecificDownload.icon}</span>}
                                        {osSpecificDownload.text}
                                    </motion.a>
                                </motion.div>
                            )}
                        </motion.div>
                    </AnimatePresence>

                    {/* Right Navigation Button (Desktop View Only) */}
                    <motion.div
                        className="walkthrough-navigation-side next"
                        variants={textColumnVariants}
                        initial="initial"
                        animate="animate"
                        exit="exit"
                    >
                        <motion.button
                            onClick={handleNextStep}
                            disabled={currentStepIndex === walkthroughData.length - 1}
                            className="walkthrough-button side-button"
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            transition={{ type: "spring", stiffness: 400, damping: 10 }}
                            aria-label="Next Step"
                        >
                            <i className="fas fa-chevron-right"></i>
                        </motion.button>
                    </motion.div>
                </div>

                {/* Mobile Navigation Buttons (Fixed to Bottom for Mobile) */}
                {/* This div is conditionally rendered by CSS display property based on media query */}
                <motion.div
                    className="walkthrough-navigation-mobile" /* Renamed for clarity */
                    initial="initial"
                    animate="animate"
                    exit="exit"
                >
                    <motion.button
                        onClick={handlePreviousStep}
                        disabled={currentStepIndex === 0}
                        className="walkthrough-button mobile-nav-button prev-button"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        transition={{ type: "spring", stiffness: 400, damping: 10 }}
                        aria-label="Previous Step"
                    >
                        <i className="fas fa-chevron-left"></i>
                    </motion.button>
                    <motion.button
                        onClick={handleNextStep}
                        disabled={currentStepIndex === walkthroughData.length - 1}
                        className="walkthrough-button mobile-nav-button next-button"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        transition={{ type: "spring", stiffness: 400, damping: 10 }}
                        aria-label="Next Step"
                    >
                        <i className="fas fa-chevron-right"></i>
                    </motion.button>
                </motion.div>

            </div>
        </motion.div>
    );
};

export default InteractiveWalkthroughPage;